/**
 * Attributes the Datadog log intake owns. A log's *root* is the intake's own
 * namespace: anything sent under one of these keys is consumed by a remapper
 * instead of being stored as a custom attribute, so a caller-supplied value
 * silently rewrites the log itself.
 *
 * The two that bite hardest:
 * - `message` — replaces the log line, so the event name disappears and the
 *   event can no longer be found by searching for it.
 * - `status` — drives the log level, so an HTTP status (`200`, `403`) moves the
 *   log out of `status:info` and out of every default Log Explorer query.
 *
 * @see https://docs.datadoghq.com/logs/log_configuration/attributes_naming_convention/#reserved-attributes
 */
const RESERVED_ATTRIBUTES: ReadonlySet<string> = new Set([
  'date',
  'ddsource',
  'ddtags',
  'env',
  'error',
  'host',
  'logger',
  'message',
  'service',
  'session_id',
  'source',
  'span_id',
  'status',
  'trace_id',
  'usr',
  'version',
  'view',
]);

/** Prefix applied to a caller attribute that collides with a reserved one. */
const COLLISION_PREFIX = 'data_';

/**
 * Find a destination key for a reserved attribute that no other attribute
 * already occupies. `{ status: 403, data_status: 'x' }` would otherwise reduce
 * to a single `data_status`, silently dropping one of the two values, so the
 * prefix is re-applied until the key is free (`data_data_status`).
 */
const findFreeKey = (
  key: string,
  data: Record<string, unknown>,
  taken: Record<string, unknown>,
): string => {
  let candidate = `${COLLISION_PREFIX}${key}`;

  // `data` is checked as well as `taken`: a non-reserved attribute keeps its
  // own name, so it may claim this key later regardless of iteration order.
  while (Object.hasOwn(data, candidate) || Object.hasOwn(taken, candidate)) {
    candidate = `${COLLISION_PREFIX}${candidate}`;
  }

  return candidate;
};

/**
 * Re-key any attribute that would collide with a Datadog reserved attribute
 * (`status` → `data_status`, `message` → `data_message`, …) so the value is
 * stored as a searchable custom attribute instead of rewriting the log.
 *
 * Non-colliding keys are passed through untouched — existing dashboards and
 * facets keep working; only the (previously broken) colliding ones move. No
 * attribute is ever dropped or overwritten.
 */
export const sanitizeLogContext = (
  data: Record<string, unknown>,
): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};

  for (const key of Object.keys(data)) {
    const safeKey = RESERVED_ATTRIBUTES.has(key)
      ? findFreeKey(key, data, sanitized)
      : key;
    sanitized[safeKey] = data[key];
  }

  return sanitized;
};
